/**
 * Bloco #77 — isolamento do cache local por conta/clínica.
 *
 * As subabas de Configurações da Agenda (serviços, locais, horários e página
 * de agendamento) guardavam um rascunho no `localStorage` com chaves globais
 * (`allodos-agenda-services`, `allodos-booking-slug`, ...). Quem entrasse com
 * outra conta no mesmo navegador herdava tudo da conta anterior — e, pior,
 * podia publicar esses dados na própria página.
 *
 * O que vale é sempre o que está no servidor (escopado pela clínica
 * autenticada). O `localStorage` é só um cache por clínica: toda chave passa a
 * carregar o identificador da clínica (`<chave>@<tenantId>`), e as chaves
 * globais antigas são descartadas no carregamento do app.
 */

let currentScope = '';

/** Define a clínica (tenant) dona do cache. Vazio = ninguém autenticado. */
export const setStorageScope = (scope: string | null | undefined): void => {
  currentScope = String(scope || '').trim();
};

export const getStorageScope = (): string => currentScope;

/**
 * Chave isolada por clínica. Sem escopo conhecido usa `@anon`, de propósito:
 * nunca cair na chave global antiga, que misturava contas.
 */
export const scopedKey = (base: string): string => `${base}@${currentScope || 'anon'}`;

const storage = (): Storage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};

export const scopedGet = (base: string): string | null => {
  try {
    return storage()?.getItem(scopedKey(base)) ?? null;
  } catch {
    return null;
  }
};

export const scopedSet = (base: string, value: string): void => {
  try {
    storage()?.setItem(scopedKey(base), value);
  } catch {
    /* quota/privado: o servidor continua sendo a fonte */
  }
};

export const scopedRemove = (base: string): void => {
  try {
    storage()?.removeItem(scopedKey(base));
  } catch {
    /* ignore */
  }
};

export const readScopedJson = <T,>(base: string): T | null => {
  try {
    const saved = scopedGet(base);
    return saved ? (JSON.parse(saved) as T) : null;
  } catch {
    return null;
  }
};

/**
 * Prefixos das chaves globais antigas (anteriores ao bloco #77) que guardavam
 * dados de configuração da agenda sem identificar a clínica.
 */
export const LEGACY_AGENDA_KEY_PREFIXES = [
  'allodos-agenda-',
  'allodos-booking-slug',
  'allodos-recess',
  'allodos-auto-booking',
  'allodos-open-days-limit',
  'allodos-min-interval',
  'allodos-min-advance-hours',
  'allodos-social-',
  'allodos-custom-links',
  'allodos-welcome-message',
  'allodos-right-',
  'allodos-comp-',
  'allodos-professionals',
  'allodos-calendar-events',
  'allodos-therapist',
];

/**
 * Remove as chaves globais antigas. Não dá para saber de qual conta elas eram,
 * então nada é migrado: o que já foi publicado está no servidor e volta de lá.
 * Devolve quantas chaves foram removidas.
 */
export const purgeLegacyAgendaKeys = (): number => {
  const ls = storage();
  if (!ls) return 0;
  const alvo: string[] = [];
  for (let i = 0; i < ls.length; i += 1) {
    const key = ls.key(i);
    if (!key || key.includes('@')) continue;
    if (LEGACY_AGENDA_KEY_PREFIXES.some((p) => key.startsWith(p))) alvo.push(key);
  }
  alvo.forEach((k) => ls.removeItem(k));
  return alvo.length;
};
